import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import type { FilingStatus } from "../dealwatch/api";

// ---- Enforcement on an ex officio (Workflow 6) case: Articles 25–27 and 33 of Federal Decree-Law No. 36 of 2023 ----

export type ViolationArticle = "Article25" | "Article26" | "Article27";
export type EnforcementOutcome = "PrivateWarning" | "AdministrativeFine" | "ReferredToProsecution";
export type EnforcementStatus = "Draft" | "Recommended" | "Approved" | "Issued";
export type EnforcementLetterKind = "rfi" | "warning" | "fine" | "referral";
export const ENFORCEMENT_LETTER_KINDS: EnforcementLetterKind[] = ["rfi", "warning", "fine", "referral"];

export interface PenaltyFactor { key: string; labelEn: string; labelAr: string; weight: number; guidanceEn: string; guidanceAr: string }

export interface EnforcementCatalogue {
  aggravating: PenaltyFactor[];
  mitigating: PenaltyFactor[];
  articles: Array<{ key: ViolationArticle; number: number; labelEn: string; labelAr: string; rangeEn: string }>;
  outcomes: Array<{ key: EnforcementOutcome; labelEn: string; labelAr: string; approver: string; approverRoles: string[]; instrument: string }>;
  salesSources: string[];
  recommenders: string[];
  referralLegalBasis: string;
  penaltyBasis: PenaltyBasisView;
}

/** Whether the penalty calculation is enabled: the Department's legal confirmation of its basis for the method in force (scenario 6.40). */
export interface PenaltyBasisView {
  enabled: boolean; refusal?: string | null; refusalAr?: string | null; canConfirm: boolean;
  /** Under the test-environment decision controls the steps are open before the confirmation: the marking every figure and step carries. */
  testEnvironment?: { en: string; ar: string } | null; methodVersion: string; articles: string[]; ranges: string; rangesAr: string;
  confirmation?: { reference: string; referenceAr?: string | null; methodVersion: string; confirmedByName: string; confirmedAt: string; note?: string | null } | null;
}

/** The statutory range and where the chosen circumstances point inside it. */
export interface PenaltyQuote {
  article: ViolationArticle; articleNumber: number; minAed: number; maxAed: number; settlementFloorAed: number; computable: boolean; salesBasisAed?: number | null; minPercent?: number | null; maxPercent?: number | null;
  basis: string; basisAr: string; legalBasis: string;
  position: number; indicativeAed: number; netWeight: number; recommendedOutcome: EnforcementOutcome; referralWorthConsidering: boolean; reasoningEn: string; reasoningAr: string;
  aggravating: PenaltyFactor[]; mitigating: PenaltyFactor[];
}

export interface EnforcementAssessment {
  id: string; caseId: string; externalDealId?: string | null; conduct: string; violationArticle: ViolationArticle; articleNumber: number;
  undertakingName?: string | null; undertakingNameAr?: string | null; targetName?: string | null; implementedOn?: string | null; discoveredOn?: string | null; thresholdBasis?: string | null;
  /** Which Article 12 threshold was met, what shows the change of control, and the officer's check of the register for a notification. */
  thresholdTest?: "Turnover" | "Share" | "Both" | null; controlEvidence?: string | null;
  registerCheckedAt?: string | null; registerCheckedByName?: string | null; registerNotificationFound?: boolean | null; registerNote?: string | null;
  salesBasisAed?: number | null; salesBasisYear?: number | null; salesBasisSource?: string | null; salesBasisNote?: string | null; computable: boolean; fineMinAed: number; fineMaxAed: number; indicativeFineAed?: number | null; netWeight: number;
  aggravatingFactors: string[]; mitigatingFactors: string[]; aggravatingNote?: string | null; mitigatingNote?: string | null;
  proposedOutcome: EnforcementOutcome; proposedFineAed?: number | null; indicatedOutcome?: EnforcementOutcome | null; referralWorthConsidering: boolean; rationaleMarkdown?: string | null; rationaleMarkdownAr?: string | null; legalBasis: string;
  status: EnforcementStatus; draftedByName?: string | null; recommendedByName?: string | null; recommendedAt?: string | null; recommendationNote?: string | null; approvedByName?: string | null; approvedAt?: string | null; approvalNote?: string | null; decisionId?: string | null; issuedAt?: string | null; rfiRequestId?: string | null;
  approverRole: string; instrument: string; outcomeLabel: string; outcomeLabelAr: string;
}

export interface EnforcementLetterSummary { id: string; templateKey: string; kind: string; status: string; subject: string; subjectAr?: string | null; recipientName: string; sentAt?: string | null; responseDueAt?: string | null; rfiRequestId?: string | null }

export interface EnforcementView {
  persisted: boolean;
  assessment: EnforcementAssessment;
  quote: PenaltyQuote | null;
  penaltyBasis: PenaltyBasisView;
  decision?: { id: string; outcome: string; decisionNumber: string; instrument: string; displayNumber: string; displayNumberAr: string; issuedAt: string; notifiedAt?: string | null; decidedByName: string; conditions: string[]; reasoningMarkdown: string; reasoningMarkdownAr?: string | null } | null;
  rfi?: { id: string; sentAt: string; dueAt: string; respondedAt?: string | null; isClosed: boolean; items: Array<{ key: string; question: string; questionAr?: string | null; respondedAt?: string | null; satisfied?: boolean | null }> } | null;
  letters: EnforcementLetterSummary[];
  deal?: { id: string; title: string; externalId: string; filingStatus: FilingStatus; score: number; completedOn?: string | null; valueAed?: number | null; combinedUaeTurnoverAed?: number | null; combinedMarketSharePercent?: number | null; thresholdBasis?: string | null } | null;
  /** delegationRefusal: why a settlement goes to the Minister – no written delegation to the Under-Secretary in force (WB-06.04). */
  permissions: { canEdit: boolean; canRecommend: boolean; canApprove: boolean; tookPart?: boolean; canReturn: boolean; canIssue: boolean; canSendLetters: boolean; approverRole: string; approverRoles: string[]; caseState: string; delegationRefusal?: string | null; delegationRefusalAr?: string | null };
}

export interface EnforcementAssessmentInput {
  violationArticle: ViolationArticle; undertakingName: string | null; undertakingNameAr: string | null; targetName: string | null; implementedOn: string | null; discoveredOn: string | null; thresholdBasis: string | null;
  salesBasisAed: number | null; salesBasisYear: number | null; salesBasisSource: string | null; salesBasisNote: string | null;
  aggravatingFactors: string[]; mitigatingFactors: string[]; aggravatingNote: string | null; mitigatingNote: string | null;
  proposedOutcome: EnforcementOutcome; proposedFineAed: number | null; rationaleMarkdown: string | null; rationaleMarkdownAr: string | null;
  thresholdTest?: string | null; controlEvidence?: string | null;
}

export interface RenderedEnforcementLetter { templateKey: string; kind: string; subjectEn: string; subjectAr: string; bodyEn: string; bodyAr: string; legalBasis?: string | null; responseDays?: number | null; responseWorkingDays: boolean }

export const enforcementKeys = {
  view: (caseId: string) => ["enforcement", caseId] as const,
  catalogue: ["enforcement-catalogue"] as const,
  quote: (article: ViolationArticle, sales: number | null, aggravating: string[], mitigating: string[]) => ["enforcement-quote", article, sales, [...aggravating].sort().join(","), [...mitigating].sort().join(",")] as const,
};

export function useEnforcementCatalogue() {
  const api = useApi();
  return useQuery({ queryKey: enforcementKeys.catalogue, queryFn: () => api.get<EnforcementCatalogue>("/api/enforcement/catalogue"), staleTime: 60 * 60 * 1000 });
}

export function useEnforcementView(caseId: string) {
  const api = useApi();
  return useQuery({ queryKey: enforcementKeys.view(caseId), queryFn: () => api.get<EnforcementView>(`/api/cases/${caseId}/enforcement`) });
}

/** Live statutory range for the form as it is edited, before anything is saved. */
export function usePenaltyQuote(article: ViolationArticle, salesBasisAed: number | null, aggravating: string[], mitigating: string[], enabled = true) {
  const api = useApi();
  return useQuery({
    // Nothing is calculated until the basis of the calculation is legally confirmed (scenario 6.40).
    enabled,
    queryKey: enforcementKeys.quote(article, salesBasisAed, aggravating, mitigating),
    queryFn: () => api.post<PenaltyQuote>("/api/enforcement/quote", { violationArticle: article, salesBasisAed, aggravatingFactors: aggravating, mitigatingFactors: mitigating }),
    placeholderData: (prev) => prev,
    staleTime: 5 * 60 * 1000,
  });
}
